import { TASK_TYPES, TASK_TYPE_LABELS, type TaskType } from '@hearth/shared';
import { clsx } from 'clsx';
import { useId, useState } from 'react';
import { Field, Input, Select, Switch } from '../../components/ui';
import { formatDateTime } from '../../lib/format';
import { FOLLOW_UP_QUICK_PICKS, followUpStart, initialFollowUp, reminderOptions, type FollowUpState } from './entryModel';

const STORAGE_KEY = 'hearth.followUp';

function rememberedEnabled(): boolean {
  try {
    // Scheduling the next step is the habit we want, so it starts on until someone turns it off.
    return window.localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

/**
 * State for `FollowUpFields`; the on/off choice is remembered across forms. Build the request with
 * `followUpBody(state, defaultTitle, new Date())`.
 */
export function useFollowUp(options: { type: TaskType; days: number }): [FollowUpState, (next: FollowUpState) => void] {
  const [state, setState] = useState(() => initialFollowUp({ ...options, enabled: rememberedEnabled(), now: new Date() }));
  const update = (next: FollowUpState) => {
    if (next.enabled !== state.enabled) {
      try {
        window.localStorage.setItem(STORAGE_KEY, next.enabled ? 'on' : 'off');
      } catch {
        // Storage can be unavailable (private mode); the choice then only lasts for this form.
      }
    }
    setState(next);
  };
  return [state, update];
}

// Notes are calendar-only information, never a next step.
const FOLLOW_UP_TYPES = TASK_TYPES.filter((type) => type !== 'note');

export type FollowUpFieldsProps = {
  value: FollowUpState;
  onChange: (next: FollowUpState) => void;
  /** Title used until the user types their own ("Call back", "Follow up: <subject>"). */
  defaultTitle: string;
};

/** "Schedule a follow-up" switch plus type, title, when (quick picks or a date and time) and reminder. */
export function FollowUpFields({ value, onChange, defaultTitle }: FollowUpFieldsProps) {
  const start = followUpStart(value, new Date());
  const set = (patch: Partial<FollowUpState>) => onChange({ ...value, ...patch });
  const whenLabelId = useId();
  const title = value.title ?? defaultTitle;

  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-3">
      <Switch
        label="Schedule a follow-up"
        description={value.enabled ? undefined : 'Put the next step on your calendar so it does not slip.'}
        checked={value.enabled}
        onChange={(enabled) => set({ enabled })}
      />
      {value.enabled && (
        <>
          <div className="grid gap-3 sm:grid-cols-[10rem_minmax(0,1fr)]">
            <Field label="Type">
              <Select
                value={value.type}
                onChange={(event) => set({ type: event.target.value as TaskType })}
                options={FOLLOW_UP_TYPES.map((type) => ({ value: type, label: TASK_TYPE_LABELS[type] }))}
              />
            </Field>
            <Field label="Title" required error={title.trim() === '' ? 'Enter a title' : undefined}>
              <Input value={title} onChange={(event) => set({ title: event.target.value })} />
            </Field>
          </div>
          <div className="space-y-2">
            <p className="text-[13px] font-medium text-slate-700" id={whenLabelId}>
              When
            </p>
            <div className="flex flex-wrap gap-1.5" role="group" aria-labelledby={whenLabelId}>
              {[...FOLLOW_UP_QUICK_PICKS, { key: 'pick' as const, label: 'Pick…' }].map((pick) => (
                <button
                  key={pick.key}
                  type="button"
                  aria-pressed={value.when === pick.key}
                  onClick={() => set({ when: pick.key })}
                  className={clsx(
                    'h-7 rounded-full px-3 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember-500',
                    value.when === pick.key ? 'bg-ember-600 text-white' : 'bg-surface text-slate-700 ring-1 ring-slate-300 hover:bg-slate-100',
                  )}
                >
                  {pick.label}
                </button>
              ))}
            </div>
            {value.when === 'pick' && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Date">
                  <Input type="date" value={value.date} onChange={(event) => set({ date: event.target.value })} />
                </Field>
                <Field label="Time">
                  <Input type="time" value={value.time} onChange={(event) => set({ time: event.target.value })} />
                </Field>
              </div>
            )}
            <p className="text-xs text-slate-500">{start ? formatDateTime(start) : 'Enter a date and time.'}</p>
          </div>
          <Field label="Reminder">
            <Select
              value={value.reminderMinutes === null ? '' : String(value.reminderMinutes)}
              onChange={(event) => set({ reminderMinutes: event.target.value === '' ? null : Number(event.target.value) })}
              options={reminderOptions(false, value.reminderMinutes)}
            />
          </Field>
        </>
      )}
    </div>
  );
}
